import { useFetchMessages } from '@faceup/localization'
import { translations as reportTranslations } from '@faceup/report'
import { usePromiseSuspense } from '@faceup/ui'
import {
  convertBasenameToLanguage,
  getValidBasename,
  type Language,
  languageEnumToBasename,
  RTL_LANGUAGES,
  SUPPORTED_BASENAMES,
} from '@faceup/utils'
import moment from 'moment-timezone'
import { type ReactNode, Suspense, useCallback, useEffect, useState } from 'react'
import { FullScreenLoader } from '../Components/FullScreenLoader'
import translations, { getDeviceInfo } from '../locales'
import useStorage, { StorageKeys } from '../utils/useStorage'
import { LanguageContext, type TextDirection } from './LanguageContext'

type Props = {
  children: ReactNode
}

type LanguageProviderInnerProps = {
  children: ReactNode
  newMessages: Record<string, string>
  handleNewMessages: (messages: Record<string, string>) => void
}

const LanguageProviderInner = ({
  children,
  newMessages,
  handleNewMessages,
}: LanguageProviderInnerProps) => {
  const storage = useStorage()
  const { getMessages } = useFetchMessages({
    handleNewMessages,
  })
  const savedLanguage = storage.get(StorageKeys.Language)

  const [basename, setBasename] = useState<string>(getValidBasename())
  const [language, setLanguage] = useState<Language>(
    convertBasenameToLanguage(basename === '/' ? (savedLanguage ?? navigator.language) : basename)
  )

  const [previousLanguage, setPreviousLanguage] = useState<Language | undefined>(language)

  const loadAllMessages = async (newLanguage: Language) => {
    const loadMessages = async () => {
      const newMessages = await translations.messages[newLanguage]()
      return newMessages.default
    }

    const loadReportMessages = async () => {
      const newMessages = await reportTranslations.messages[newLanguage]()
      return newMessages.default
    }

    const loadLocale = async () => {
      const newAntLocale = await translations.ant[newLanguage]()
      return newAntLocale.default
    }

    const messages = await Promise.all([
      loadMessages(),
      loadReportMessages(),
      loadLocale(),
      getMessages(newLanguage),
    ])
    return {
      messages: { ...messages[0], ...messages[1], ...messages[3] },
      antLocale: messages[2],
    }
  }

  const { antLocale, messages } = usePromiseSuspense(loadAllMessages, [language])

  const replaceBasename = (newBasename: string) => {
    const {
      location: { pathname, search, hash },
      history,
    } = window

    const maybeBasename = pathname.split('/')?.slice(1)?.[0] ?? ''
    const hasBasename = SUPPORTED_BASENAMES.includes(maybeBasename)
    const newPathname = `${newBasename}${
      hasBasename ? `/${pathname.split('/').slice(2).join('/')}` : pathname
    }${search}${hash}`

    history.replaceState(newPathname, '', newPathname)
  }

  const getDirection = (language: Language): TextDirection =>
    RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'

  const setHtmlAttributes = (basename: string, newLanguage: Language) => {
    document.documentElement.setAttribute('lang', basename)
    document.documentElement.setAttribute('dir', getDirection(newLanguage))
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: All helper functions are stable and don't need to be in deps
  const changeLanguage = useCallback(async (newLanguage: Language) => {
    const basename = languageEnumToBasename(newLanguage)
    moment.locale(basename)
    setLanguage(newLanguage)
    storage.set(StorageKeys.Language, newLanguage, true)
    setBasename(`/${basename}`)
    replaceBasename(`/${basename}`)
    setHtmlAttributes(basename, newLanguage)
  }, [])

  useEffect(() => {
    const userLanguage = convertBasenameToLanguage(
      basename === '/' ? (savedLanguage ?? navigator.language) : basename
    )

    changeLanguage(userLanguage)
  }, [basename, changeLanguage, savedLanguage])

  const changeLanguageByDeviceInfo = useCallback(async () => {
    const { platform, language } = await getDeviceInfo()

    if (platform !== 'web' && !savedLanguage) {
      changeLanguage(convertBasenameToLanguage(language))
    }
  }, [changeLanguage, savedLanguage])

  useEffect(() => {
    changeLanguageByDeviceInfo()
  }, [changeLanguageByDeviceInfo])

  if (basename === '/') {
    return <FullScreenLoader />
  }

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        changePreviousLanguage: setPreviousLanguage,
        messages: { ...messages, ...newMessages },
        basename,
        antLocale,
        previousLanguage,
        direction: language ? getDirection(language) : 'ltr',
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export const LanguageProvider = ({ children }: Props) => {
  const [newMessages, setNewMessages] = useState<Record<string, string>>({})

  const handleNewMessages = useCallback((messages: Record<string, string>) => {
    setNewMessages(messages)
  }, [])

  // usePromiseSuspense unmounts and remounts the component
  // so we need to have state outside of the component
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <LanguageProviderInner newMessages={newMessages} handleNewMessages={handleNewMessages}>
        {children}
      </LanguageProviderInner>
    </Suspense>
  )
}
